import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "./assets/dentLAB_logo.svg";
import "./layout.css";
import clsx from "clsx";
import { Toaster } from "./components/ui/sonner";
import { useCentralStore } from "./store/Central";
import { Loading } from "../../components/src/animations/loading";
import { Login as LoginPage } from "./pages/login/login.page";
import { supabase } from "../../lib/supabase/supabaseClient";
import { Logger } from "../../lib/logger/Logger";
import { EnterTotp } from "./pages/login/enter-totp.page";
import {
	Assignment,
	AssignmentOutlined,
	Home,
	HomeOutlined,
	PeopleAlt,
	PeopleAltOutlined,
} from "@mui/icons-material";
import { Avatar, IconButton } from "@mui/material";
import { Search } from "lucide-react";
import { SearchDialogComponent } from "./components/Dialog/search-dialog.component";

const Tabs = [
	{
		name: "Actions",
		passiveIcon: <HomeOutlined sx={{ fontSize: 32, color: "gray" }} />,
		activeIcon: <Home sx={{ fontSize: 32, color: "black" }} />,
		description: "Menü",
		path: "/actions",
	},
	{
		name: "Patients",
		passiveIcon: <PeopleAltOutlined sx={{ fontSize: 32, color: "gray" }} />,
		activeIcon: <PeopleAlt sx={{ fontSize: 32, color: "black" }} />,
		path: "/patients",
		description: "Patienten",
	},
	{
		name: "Jobs",
		passiveIcon: (
			<AssignmentOutlined sx={{ fontSize: 32, color: "gray" }} />
		),
		activeIcon: <Assignment sx={{ fontSize: 32, color: "black" }} />,
		description: "Aufträge",
		path: "/jobs",
	},
];

export const Layout = ({ children }: { children: React.ReactNode }) => {
	const location = useLocation();
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();
	const { initialize, profile, avatarUrl, userId } =
		useCentralStore.getState();
	const [mfaVerified, setMFAVerified] = useState<boolean>(false);
	const [mfaAuthenticated, setMFAAuthenticated] = useState<boolean>(false);
	const [mfaEnrolled, setMFAEnrolled] = useState<boolean>(false);
	const [searchDialogOpen, setSearchDialogOpen] = useState<boolean>(false);

	const verifyMFA = async () => {
		const { data, error } =
			await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
		let mfaVerified = false,
			mfaAuthenticated = false,
			mfaEnrolled = false;
		if (error) {
			Logger.error(error);
			return {
				mfaAuthenticated,
				mfaEnrolled,
				mfaVerified,
			};
		}

		if (data.nextLevel === "aal2" && data.nextLevel !== data.currentLevel) {
			mfaEnrolled = true;
			mfaAuthenticated = false;
		} else if (
			data.nextLevel === "aal1" &&
			data.nextLevel === data.currentLevel
		) {
			mfaEnrolled = false;
			mfaAuthenticated = true;
		} else if (
			data.nextLevel === "aal2" &&
			data.nextLevel === data.currentLevel
		) {
			mfaEnrolled = true;
			mfaAuthenticated = true;
		}
		mfaVerified = true;

		setMFAVerified(mfaVerified);
		setMFAEnrolled(mfaEnrolled);
		setMFAAuthenticated(mfaAuthenticated);
	};

	const needsMFA = mfaVerified && mfaEnrolled && !mfaAuthenticated;

	useEffect(() => {
		setLoading(true);
		(async () => {
			await initialize();
			await verifyMFA();
			setLoading(false);
		})();
	}, []);

	if (!/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
		return (
			<div className="text-white h-screen flex items-center justify-center">
				Diese Website ist nur auf Smartphones verfügbar.
			</div>
		);
	}

	return (
		<div className="w-full h-full fixed">
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginLeft: "1rem",
					padding: "3px 8px",
					height: "8vh",
				}}
			>
				<img
					src={logo}
					style={{
						height: "14px",
					}}
				/>
				<div className="flex items-center gap-4">
					<IconButton
						onClick={() => {
							setSearchDialogOpen(true);
						}}
					>
						<Search size={24} className="text-white" />
					</IconButton>
					<Avatar
						sx={{
							width: 40,
							height: 40,
						}}
						onClick={() => {
							if (profile && !needsMFA) {
								navigate("/profile");
							}
						}}
						alt="avatar"
						src={avatarUrl}
					/>
				</div>
			</div>
			<div className="bg-white rounded-t-lg h-[92vh] w-full">
				<div className="h-[82vh]">
					{loading ? (
						<Loading />
					) : !profile ? (
						<LoginPage />
					) : profile && needsMFA ? (
						<EnterTotp />
					) : location.pathname.includes("/scan") ? (
						<div className="h-full">{children}</div>
					) : (
						<div className="pt-4 pl-4 pr-4 pb-1 h-full">
							{children}
							<SearchDialogComponent
								open={searchDialogOpen}
								onClose={() => setSearchDialogOpen(false)}
							/>
						</div>
					)}
					<Toaster />
				</div>
				{location.pathname !== "/login" && userId && !needsMFA && (
					<div className="border-x h-[8vh] flex justify-between border-t border-gray-200">
						{Tabs.map((tab) => (
							<div
								onClick={() => {
									if (profile) {
										navigate(tab.path);
									}
								}}
								key={tab.name}
								className={clsx(
									"p-1 flex w-full cursor-pointer bg-white flex-col justify-center items-center"
								)}
							>
								{tab.path === location.pathname ? (
									<>
										{tab.activeIcon}
										<div className="text-xs text-black font-bold">
											{tab.description}
										</div>
									</>
								) : (
									<>
										{tab.passiveIcon}
										<div className="text-xs text-gray-500">
											{tab.description}
										</div>
									</>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};
